import {
    Button, ClickEvent, DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent, DropdownItem,
    DropdownSelectionEvent, Layout, LookupModelSelectionEvent, SlideoutDecorator, Table, Textbox, ValidationResult
} from "@mcleod/components";
import { AutogenLayoutFuelSurchargeOptionsSlideout } from "./autogen/AutogenLayoutFuelSurchargeOptionsSlideout";
import { Alignment, Api, DatePart, DateUtil, ModelRow, StringUtil, UrlUtil } from "@mcleod/core";
import { CustomFuelSurchargeTableMaster } from "./fuelsurcharge/CustomFuelSurchargeTableMaster";

enum RateType {
    NONE = "N",
    WEEKLY = "Y",
    MONTHLY = "M",
    CUSTOM = "Z"
}

enum Method {
    PERCENTAGE = "P",
    DISTANCE = "D",
    FLAT = "F",
    TABLE = "T"
}

export class FuelSurchargeOptionsSlideout extends AutogenLayoutFuelSurchargeOptionsSlideout {
    private _currentTable: Table;

    onLoad(): void | Promise<void> {
        this.textboxMethod.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
            if (mode === DataSourceMode.SEARCH) {
                return null;
            }
            const result: ValidationResult = {
                component: this.textboxMethod,
                isValid: true
            }
            if (this.textboxApplyDoeRate.selectedItem && (this.textboxApplyDoeRate.selectedItem.value === RateType.WEEKLY || this.textboxApplyDoeRate.selectedItem.value === RateType.MONTHLY)) {
                if (this.textboxMethod.selectedItem && this.textboxMethod.selectedItem.value === Method.FLAT) {
                    result.isValid = false;
                    result.validationMessage = "Surcharge method [" + this.textboxMethod.text + "] is invalid when the DOE rate type '"
                        + this.textboxApplyDoeRate.text + "' is chosen."
                    return result;

                }
                if (this.textboxMethod.selectedItem && (this.textboxMethod.selectedItem.value === Method.DISTANCE || this.textboxMethod.selectedItem.value === Method.PERCENTAGE)) {
                    if (StringUtil.isEmptyString(this.textboxCustomCalc.text)) {
                        if (StringUtil.isEmptyString(this.textboxBasePrice.text) || StringUtil.isEmptyString(this.textboxFuelVariance.text)) {
                            result.isValid = false;
                            result.validationMessage = "When using DOE rates and Surcharge method is [" + this.textboxMethod.text
                                + "] you must provide either a custom calculation or Base and Variance."
                            return result;
                        }
                    } else if (this.textboxCustomCalc.text?.includes("Variance") && StringUtil.isEmptyString(this.textboxFuelVariance.text)) {
                        result.isValid = false;
                        result.validationMessage = "Your custom calculation [" + this.textboxCustomCalc.text
                            + "] requires a value for fuel price variance.";
                        return result;
                    } else if (this.textboxCustomCalc.text?.includes("Base") && StringUtil.isEmptyString(this.textboxBasePrice.text)) {
                        result.isValid = false;
                        result.validationMessage = "Your custom calculation [" + this.textboxCustomCalc.text
                            + "] requires a value for fuel price base."
                        return result;
                    }
                }
            } else if (this.textboxMethod.selectedItem && this.textboxMethod.selectedItem.value === Method.TABLE) {
                if (StringUtil.isEmptyString(this.textboxCustomFscHdrId.text)) {
                    result.isValid = false;
                    result.validationMessage = "Surcharge method [" + this.textboxMethod.text + "] is invalid when the DOE rate type is '"
                        + this.textboxApplyDoeRate.text + "'."
                    return result;
                }
            }
            return result;
        };
        this.textboxSurchgEnddt.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
            return this.dateValidator(false, this.textboxSurchgStrtdt, this.textboxSurchgEnddt);
        }
        this.mainDataSource.addAfterExecutionListener((event: DataSourceExecutionEvent) => {
            const urlProp = UrlUtil.getPropsFromUrl(window.location.search)
            if (urlProp != null && urlProp.surcharge_type != null) {
                this.configureLayoutForSurchargeType(urlProp.surcharge_type);
            }
        });
    }

    sourceCustomerFuelAfterModeChange(event: DataSourceModeChangeEvent) {
        if (!StringUtil.isEmptyString(this.mainDataSource.activeRow.get("custom_fsc_index_id"))) {
            Api.post("lme/ar/custom-fuel-region", { fuel_index_id: this.mainDataSource.activeRow.get("custom_fsc_index_id") }).then((response) => {
                if (response.data[0]?.custom_fuel_region) {
                    this.fillItems(response.data[0]?.custom_fuel_region);
                    this.textboxCustomFscRegionId.selectedItem = (this.textboxCustomFscRegionId.items as DropdownItem[])?.find(item => item.value === this.mainDataSource.activeRow.get("custom_fsc_region_id"));
                }
            });
        }
        if (event.newMode !== DataSourceMode.SEARCH) {
            this.syncLayoutWithMethodAndRateType(this.mainDataSource.activeRow.get("method"), this.mainDataSource.activeRow.get("apply_doe_rate"));
        }
    }

    onChargeCodeLookupModelSelection(event: LookupModelSelectionEvent) {
        this.activeRow.set("charge_code.descr", event.hasSelection() ? event.selection?.get("descr") : null);
    }

    beforeLookupModelSelection(event) {
        event.filter.is_fuel_surcharge = "Y";
    }

    rateTypeAfterDropdownSelection(event: DropdownSelectionEvent) {
        if (!event.hasSelection()) {
            return;
        }

        const rateType = event.selection.value;

        // Reset all fields to default state
        this.syncLayoutWithRateType(rateType);
    }

    syncLayoutWithRateType(rateType: string) {
        const blank = StringUtil.isEmptyString(rateType);
        const isCustomRateType = !blank && rateType === RateType.CUSTOM;
        const isNoneRateType = !blank && rateType === RateType.NONE;
        const isWeeklyRateType = !blank && rateType === RateType.WEEKLY;
        const isMonthlyRateType = !blank && rateType === RateType.MONTHLY;

        this.textboxCustomFscIndexId.visible = isCustomRateType;
        this.textboxCustomFscRegionId.visible = isCustomRateType && this.textboxCustomFscIndexId.getDataValue() != '';
        this.textboxFscCycleCode.visible = isWeeklyRateType;

        this.textboxDelayDays.visible = !isNoneRateType;
        this.textboxCustomFscHdrId.visible = !isNoneRateType;
        this.textboxFuelVariance.visible = !isNoneRateType;
        this.textboxBasePrice.visible = !isNoneRateType;
        this.textboxRoundSurchgCalc.visible = !isNoneRateType;

        this.textboxRegion.required = isWeeklyRateType || isMonthlyRateType;
        this.textboxDelayDays.required = isWeeklyRateType || isMonthlyRateType;

        if (!isCustomRateType) {
            this.mainDataSource.activeRow.set("custom_fsc_index_id", null);
            this.textboxCustomFscRegionId.selectedItem = null;
            this.mainDataSource.activeRow.set("custom_fsc_region_id", null);
        }

        if (isNoneRateType) {
            this.activeRow?.set("delay_days", null);
            this.activeRow?.set("custom_fsc_hdr_id", null);
            this.activeRow?.set("custom_calc", null);
            this.activeRow?.set("fuel_variance", null);
            this.activeRow?.set("base_price", null);
            this.textboxRoundSurchgCalc.selectedItem = null;
            this.activeRow?.set("round_surchg_calc", null);
        }

        if (!isWeeklyRateType) {
            this.mainDataSource.activeRow.set("fsc_cycle_code", null);
        }
    }

    async customFuelSurchargeIndexIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            this.textboxCustomFscRegionId.visible = true
            this.textboxCustomFscRegionId.enabled = true;
            Api.post("lme/ar/custom-fuel-region", { fuel_index_id: event.selection?.get("id") }).then((response) => {
                if (response.data[0]?.custom_fuel_region) {
                    this.fillItems(response.data[0]?.custom_fuel_region);
                }
            });
        } else {
            this.textboxCustomFscRegionId.visible = false;
            this.textboxCustomFscRegionId.enabled = false;
            this.textboxCustomFscRegionId.selectedItem = null;
            this.mainDataSource.activeRow.set("custom_fsc_region_id", null);
        }
    }

    private fillItems(items: any) {
        const customFuelRegion: any[] = items
        this.textboxCustomFscRegionId.items = customFuelRegion.map((item) => {
            return new DropdownItem(item.dbValue, item.displayValue);
        });
    }

    textboxMethodAfterDropdownSelection(event: DropdownSelectionEvent) {
        if (!event.hasSelection()) {
            return;
        }
        const fuelSurchargeMethod: string = event.selection.value as string;
        const rateType: string = this.mainDataSource.activeRow.getString("apply_doe_rate", null);
        this.syncLayoutWithMethodAndRateType(fuelSurchargeMethod, rateType)
    }

    syncLayoutWithMethodAndRateType(fuelSurchargeMethod: string, rateType: string) {
        // Set default field states
        this.syncLayoutWithRateType(rateType);

        const blank = StringUtil.isEmptyString(fuelSurchargeMethod);
        const isMethodPercentage = !blank && fuelSurchargeMethod === Method.PERCENTAGE;
        const isMethodDistance = !blank && fuelSurchargeMethod === Method.DISTANCE;
        const isMethodFlat = !blank && fuelSurchargeMethod === Method.FLAT;
        const isMethodTable = !blank && fuelSurchargeMethod === Method.TABLE;

        this.textboxFuelPercent.visible = isMethodPercentage;
        this.textboxFuelPercent.required = isMethodPercentage;
        if (!isMethodPercentage) {
            this.sourceCustomerFuelOptions.activeRow?.set("fuel_percent", null);
        }

        this.textboxAmount.visible = isMethodDistance || isMethodFlat;
        this.textboxAmount.required = isMethodDistance || isMethodFlat;
        if (!isMethodDistance && !isMethodFlat) {
            this.sourceCustomerFuelOptions.activeRow?.set("amount", null);
        }

        this.textboxCustomFscHdrId.visible = isMethodTable;
        this.textboxCustomFscHdrId.required = isMethodTable;
        this.buttonAddCustomFuelSurchargeTable.visible = isMethodTable;
        if (!isMethodTable) {
            this.sourceCustomerFuelOptions.activeRow?.set("custom_fsc_hdr_id", null);
        }
    }

    private dateValidator(isArrive: boolean, startComp: Textbox, endComp: Textbox): ValidationResult {
        if (!StringUtil.isEmptyString(startComp.text) && !StringUtil.isEmptyString(endComp.text)) {
            const arriveDate = DateUtil.parseDateWithKeywords(startComp.text, true, true);
            const departDate = DateUtil.parseDateWithKeywords(endComp.text, true, true);
            const dayDiff = DateUtil.dateDiff(DatePart.SECOND, departDate, arriveDate);
            if (dayDiff < 0) {
                return {
                    component: isArrive ? startComp : endComp,
                    isValid: false,
                    validationMessage: "The end date cannot be prior to the start date."
                };
            } else {
                startComp.resetValidation();
                endComp.resetValidation();
            }
        }
        return null;
    }

    set currentTable(value: Table) {
        this._currentTable = value;
    }

    get currentTable(): Table {
        return this._currentTable;
    }

    customValidation(): ValidationResult {
        let orderType = this.textboxOrderTypeId.text;
        if (StringUtil.isEmptyString(orderType)) {
            orderType = "no_order_type";
        }
        let truckloadLTL = this.textboxTruckloadLtl.selectedItem?.value;
        if (StringUtil.isEmptyString(truckloadLTL)) {
            truckloadLTL = "no_truckload_ltl";
        }
        const start = DateUtil.parseDateWithKeywords(this.textboxSurchgStrtdt.text, true, true);
        const endDate = DateUtil.parseDateWithKeywords(this.textboxSurchgEnddt.text, true, true);
        const id = this.mainDataSource.activeRow.get("id");
        for (let index = 0; index < this.currentTable?.rows?.length; index++) {
            const element = this.currentTable.rows[index];
            const modelRow = element.data as ModelRow;
            if (orderType !== modelRow.get("order_type_id", "no_order_type")) {
                continue;
            }
            if (id !== null && id === modelRow.get("id")) {
                continue
            }
            const tlLTL = modelRow.get("truckload_ltl", "");
            if ((tlLTL !== "B" && tlLTL !== "no_truckload_ltl") &&
                (truckloadLTL !== "B" && truckloadLTL !== "no_truckload_ltl")) {
                continue;

            }
            const modelRowStart = new Date(modelRow.get("surchg_strtdt"));
            const modelRowEnd = new Date(modelRow.get("surchg_enddt"));
            const endDateIsNull = modelRow.get("surchg_enddt") === null || modelRow.get("surchg_enddt") === undefined;
            if (endDate === null) {
                if (endDateIsNull) {
                    return this.showErrorMessage(orderType, truckloadLTL);
                } else if (DateUtil.dateDiff(DatePart.SECOND, modelRowStart, start) < 1) {
                    return this.showErrorMessage(orderType, truckloadLTL);
                }
            } else {
                if (endDateIsNull) {
                    if (DateUtil.dateDiff(DatePart.SECOND, endDate, modelRowStart) < 1) {
                        return this.showErrorMessage(orderType, truckloadLTL);
                    }
                } else {
                    if (DateUtil.dateDiff(DatePart.SECOND, modelRowStart, start) >= 0 &&
                        DateUtil.dateDiff(DatePart.SECOND, modelRowEnd, start) < 1) {
                        return this.showErrorMessage(orderType, truckloadLTL);
                    } else if (DateUtil.dateDiff(DatePart.SECOND, modelRowStart, endDate) >= 0 &&
                        DateUtil.dateDiff(DatePart.SECOND, modelRowEnd, endDate) < 1) {
                        return this.showErrorMessage(orderType, truckloadLTL);
                    } else if (DateUtil.dateDiff(DatePart.SECOND, modelRowStart, start) < 1 &&
                        DateUtil.dateDiff(DatePart.SECOND, modelRowEnd, endDate) >= 0) {
                        return this.showErrorMessage(orderType, truckloadLTL);
                    }
                }
            }
        }

        const rateType = this.textboxApplyDoeRate.selectedItem?.value;
        const method = this.textboxMethod.selectedItem?.value;
        if(method === Method.TABLE && rateType === RateType.NONE) {
            return {
                component: this.textboxApplyDoeRate,
                isValid: false,
                validationMessage: `Rate type of 'None' is not valid when surcharge method is 'Table'.`
            };
        }

        return null;
    }

    private showErrorMessage(orderType: string, truckLoad: string): ValidationResult {
        return {
            component: this.textboxOrderTypeId,
            isValid: false,
            validationMessage: `Fuel surcharge for customer [${this.mainDataSource.activeRow.get("customer_id")}], order type [${orderType}] and TL/LTL [${truckLoad}] already exists for this date range.`
        };
    }

    addCustomerTableOnClick(event: ClickEvent) {
        const customFuelSurchargeTableMaster: CustomFuelSurchargeTableMaster = Layout.getLayout("lme/ar/fuelsurcharge/CustomFuelSurchargeTableMaster") as CustomFuelSurchargeTableMaster;
        const acceptButton = customFuelSurchargeTableMaster.acceptButton;
        customFuelSurchargeTableMaster.acceptButtonCallback = () => {
            customFuelSurchargeTableMaster.mainDataSource.post();
            customFuelSurchargeTableMaster.slideOut();
        };
        const button = event.target as Button;
        button.busy = true;
        const slideout = new SlideoutDecorator({
            title: `Custom Fuel Surcharge Table`,
            layout: customFuelSurchargeTableMaster,
            layoutLoadListeners: (event) => {
                customFuelSurchargeTableMaster.mainDataSource.mode = DataSourceMode.ADD;
            },
            fillVerticalSpace: true,
            fillHorizontalSpace: true,
            transitionOptions: { direction: Alignment.CENTER },
            overlayProps: { closeOnClickOff: false, greyedBackground: true },
            addlComponents: [acceptButton],
            onClose: (cancelled: boolean) => customFuelSurchargeTableMaster.slideOut(),
            doAfterSlideIn: () => button.busy = false
        });
    }

    configureLayoutForSurchargeType(surchargeType: string) {
        const isCarrierSurcharge = StringUtil.equalsIgnoreCase(surchargeType, 'R');
        this.textboxFuelSurchargeId.required = !isCarrierSurcharge;
        this.textboxFuelSurchargeId.visible = !isCarrierSurcharge;
        this.switchCredit.visible = !isCarrierSurcharge;
        this.textboxOrderTypeId.visible = !isCarrierSurcharge;
        this.textboxTruckloadLtl.visible = !isCarrierSurcharge;
        this.textboxFuelSurchargePayId.visible = isCarrierSurcharge;
        this.textboxFuelSurchargePayId.required = isCarrierSurcharge;
        this.textboxDescr.visible = isCarrierSurcharge;
        this.textboxDeductCodeId.visible = isCarrierSurcharge;
        this.textboxDeductCodeId.required = isCarrierSurcharge;
    }
}
